// Menu principal: modos de jogo, sala com amigos, conta.
import { api, errorMessage } from "../api";
import { joinMatch, queueMatch, rejoinMatch, savedRoom, setCurrentRoom, type Mode } from "../net";
import { navigate, type View } from "../router";
import { currentUser, logout, setUser } from "../session";
import { esc, screen } from "../ui";
import { num, t } from "../i18n";
import { CRYSTAL_ICON } from "./shop";
import { PDX } from "./market";
import { feature } from "../features";
import { CHANGELOG, hasUnseenChangelog } from "../changelog";
import { speciesName } from "../data";

export const DEFAULT_AVATAR = 25;
import { GameStore, setGameStore } from "../game/store";
import { spriteHtml, loadSprites } from "../sprites";

export const menuView: View = (root, { query }) => {
  const u = currentUser()!;
  const welcome = query.get("bemvindo");
  root.innerHTML = screen(t("menu.title"), null, `
    <section class="bw-panel profile">
      <button class="avatar-btn ${u.frame ? `frame-${esc(u.frame)}` : ""}" data-avatar aria-label="${t("avatar.change")}" title="${t("avatar.change")}"></button>
      <div>
        <b>${esc(u.nickname)}</b> <span class="bw-dim">@${esc(u.username)}</span><br>
        ${u.title ? `<span class="trainer-title">«${esc(u.title)}»</span><br>` : ""}
        <span class="bw-dim">${t("menu.accountLevel", { n: u.accountLevel })}</span> · <a class="pdx-chip" href="#/mercado" title="Pokédollar">${PDX}${num(u.pokedollars ?? 0)}</a>${feature("vipShop") ? ` · <a class="crystals-chip" href="#/loja?aba=crystals">${CRYSTAL_ICON}${num(u.crystals ?? 0)}</a>` : ""}
      </div>
      <button class="bw-btn" data-logout>${t("menu.logout")}</button>
    </section>
    ${welcome ? `<div class="bw-alert bw-alert--ok">${t("menu.welcome", { name: esc(u.nickname) })}</div>` : ""}
    ${feature("changelog") ? `<a class="bw-alert bw-alert--info news-link" href="#/novidades">${hasUnseenChangelog() ? `<span class="board-badge new-badge">${t("changelog.new")}</span> ` : ""}${t("changelog.link", { v: CHANGELOG[0]!.version })}</a>` : ""}
    <div data-alert hidden></div>
    <div data-queue></div>
    <div data-rejoin></div>
    <div class="menu-grid">
      <button class="bw-btn bw-btn--blue mode-btn" data-mode="normal"><span data-sprite="133"></span><span>${t("menu.play")}<small>${t("menu.play.sub")}</small></span></button>
      <button class="bw-btn bw-btn--red mode-btn" data-mode="ranked"><span data-sprite="150"></span><span>${t("menu.ranked")}<small>${t("menu.ranked.sub")}</small></span></button>
      <a class="bw-btn mode-btn" href="#/deck"><span data-sprite="35"></span><span>${t("menu.deck")}<small>${t("menu.deck.sub")}</small></span></a>
      <a class="bw-btn bw-btn--gold mode-btn" href="#/missoes"><span data-sprite="54"></span><span>${t("menu.missions")}<small>${t("menu.missions.sub")}</small></span><span class="board-badge new-badge" data-missions-badge hidden></span></a>
      <a class="bw-btn mode-btn" href="#/ranking"><span data-sprite="149"></span><span>${t("menu.ranking")}<small>${t("menu.ranking.sub")}</small></span></a>
      <a class="bw-btn mode-btn" href="#/colecao"><span data-sprite="6" data-shiny></span><span>${t("menu.collection")}<small>${t("menu.collection.sub")}</small></span></a>
      <a class="bw-btn bw-btn--green mode-btn" href="#/criadouro"><span data-sprite="132"></span><span>${t("menu.creche")}<small>${t("menu.creche.sub")}</small></span></a>
      <a class="bw-btn mode-btn" href="#/conquistas"><span data-sprite="143"></span><span>${t("menu.achievements")}<small>${t("menu.achievements.sub")}</small></span><span class="board-badge new-badge" data-ach-badge hidden></span></a>
      <a class="bw-btn mode-btn" href="#/tutorial"><span data-sprite="25"></span><span>${t("menu.tutorial")}<small>${t("menu.tutorial.sub")}</small></span></a>
      <a class="bw-btn mode-btn" href="#/mercado"><span class="mode-gem pdx-big">₽</span><span>${t("menu.market")}<small>${t("menu.market.sub")}</small></span></a>
${feature("vipShop") ? `<a class="bw-btn bw-btn--gold mode-btn" href="#/loja"><span class="mode-gem">${CRYSTAL_ICON}</span><span>${t("menu.shop")}<small>${t("menu.shop.sub")}</small></span></a>` : ""}
    </div>
    ${u.role === "moderator" || u.role === "admin" ? `<a class="bw-btn bw-btn--red" href="#/moderacao">🛡 ${t("menu.moderation")}</a>` : ""}
    <section class="bw-panel bw-stack">
      <div class="bw-panel__header">${t("menu.friends")}</div>
      <p class="bw-dim" style="margin:0">${t("menu.friends.text")}</p>
      <form class="friends" data-friends>
        <input class="bw-input" name="code" maxlength="12" placeholder="${t("menu.friends.placeholder")}" autocomplete="off" autocapitalize="characters">
        <button class="bw-btn bw-btn--green" type="submit">${t("menu.friends.join")}</button>
      </form>
    </section>`);

  const alert = root.querySelector<HTMLElement>("[data-alert]")!;
  const show = (msg: string, kind = "error") => {
    alert.className = `bw-alert bw-alert--${kind}`;
    alert.textContent = msg;
    alert.hidden = false;
  };

  void loadSprites().then(() => {
    root.querySelector("[data-avatar]")!.innerHTML = spriteHtml(u.avatarSpecies ?? DEFAULT_AVATAR, 56) + `<span class="avatar-edit">✎</span>`;
    root.querySelectorAll<HTMLElement>("[data-sprite]").forEach((el) => (el.innerHTML = spriteHtml(Number(el.dataset.sprite), 56, el.hasAttribute("data-shiny"))));
  });

  // missões prontas para resgatar
  void api.missions().then((m) => {
    const n = m.missions.filter((x) => !x.claimed && x.progress >= x.target).length;
    const b = root.querySelector<HTMLElement>("[data-missions-badge]");
    if (b && n) {
      b.textContent = String(n);
      b.hidden = false;
    }
  }).catch(() => undefined);

  // conquistas novas
  void api.achievementsUnseen().then((r) => {
    const b = root.querySelector<HTMLElement>("[data-ach-badge]");
    if (b && r.unseen) {
      b.textContent = String(r.unseen);
      b.hidden = false;
    }
  }).catch(() => undefined);

  // ------------------------------------------------- foto de perfil
  root.querySelector("[data-avatar]")!.addEventListener("click", async () => {
    let list: number[] = [];
    try {
      list = (await api.avatars()).species;
    } catch (err) {
      return show(errorMessage(err));
    }
    await loadSprites();
    const modal = document.createElement("div");
    modal.className = "preview-modal";
    modal.innerHTML = `<div class="bw-panel avatar-box">
      <div class="bw-panel__header">${t("avatar.title")}</div>
      <input class="bw-input" data-filter placeholder="${t("avatar.search")}" autocomplete="off">
      <div class="avatar-grid" data-grid></div>
      <button class="bw-btn" data-close>${t("common.close")}</button></div>`;
    document.body.appendChild(modal);
    const grid = modal.querySelector<HTMLElement>("[data-grid]")!;
    const draw = (q = "") => {
      const needle = q.trim().toLowerCase();
      grid.innerHTML = list
        .filter((sp) => !needle || (speciesName(sp) ?? "").toLowerCase().includes(needle) || String(sp) === needle)
        .map((sp) => `<button class="avatar-opt" data-sp="${sp}" ${sp === (currentUser()?.avatarSpecies ?? DEFAULT_AVATAR) ? 'aria-selected="true"' : ""} title="${esc(speciesName(sp) ?? String(sp))}">${spriteHtml(sp, 56)}<span>${esc(speciesName(sp) ?? String(sp))}</span></button>`)
        .join("");
    };
    draw();
    const close = () => modal.remove();
    modal.querySelector<HTMLInputElement>("[data-filter]")!.addEventListener("input", (e) => draw((e.target as HTMLInputElement).value));
    modal.addEventListener("click", async (e) => {
      const el = e.target as HTMLElement;
      if (el === modal || el.closest("[data-close]")) return close();
      const opt = el.closest<HTMLElement>("[data-sp]");
      if (!opt) return;
      try {
        const r = await api.setAvatar(Number(opt.dataset.sp));
        setUser({ ...currentUser()!, avatarSpecies: r.avatarSpecies });
        root.querySelector("[data-avatar]")!.innerHTML = spriteHtml(r.avatarSpecies ?? DEFAULT_AVATAR, 56) + `<span class="avatar-edit">✎</span>`;
        close();
        show(t("avatar.saved"), "ok");
      } catch (err) {
        close();
        show(errorMessage(err));
      }
    });
  });

  root.querySelector("[data-logout]")!.addEventListener("click", async () => {
    await logout();
    navigate("/entrar");
  });
  root.querySelector("[data-resend]")?.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    btn.setAttribute("aria-busy", "true");
    try {
      await api.resendVerification();
      show(t("menu.linkSent"), "ok");
    } catch (err) {
      show(errorMessage(err));
    } finally {
      btn.removeAttribute("aria-busy");
    }
  });

  // fila por MMR (normal/ranqueada)
  let searching: AbortController | null = null;
  const queueBox = root.querySelector<HTMLElement>("[data-queue]")!;
  const fmt = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}`;
  const search = async (mode: "normal" | "ranked") => {
    if (searching) return;
    searching = new AbortController();
    const ctl = searching;
    queueBox.innerHTML = `<div class="bw-panel queue-panel"><span class="queue-ball" aria-hidden="true"></span>
      <div><b>${t(mode === "ranked" ? "queue.searchingRanked" : "queue.searching")}</b><br><span class="bw-dim small" data-q>${t("queue.starting")}</span></div>
      <button class="bw-btn" data-cancel>${t("queue.cancel")}</button></div>`;
    queueBox.querySelector("[data-cancel]")!.addEventListener("click", () => ctl.abort());
    root.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((b) => (b.disabled = true));
    try {
      const room = await queueMatch(mode, (p) => {
        const el = queueBox.querySelector("[data-q]");
        if (el) el.textContent = t("queue.progress", { time: fmt(p.waitedMs), n: p.queued, range: p.window });
      }, ctl.signal);
      if (room) {
        setCurrentRoom(room);
        setGameStore(new GameStore(room));
        navigate("/fila");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      show(/connect|fetch|ECONN|Failed/i.test(msg) ? t("menu.err.connect") : errorMessage(err));
    } finally {
      searching = null;
      queueBox.innerHTML = "";
      root.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((b) => (b.disabled = false));
    }
  };

  const enter = async (mode: Mode, extra: Record<string, unknown> = {}, btn?: HTMLElement) => {
    if (mode === "normal" || mode === "ranked") return search(mode);
    btn?.setAttribute("aria-busy", "true");
    try {
      const room = await joinMatch(mode, extra);
      setCurrentRoom(room);
      setGameStore(new GameStore(room));
      navigate("/fila");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      show(/e-mail/.test(msg) ? t("menu.err.verify") : /connect|fetch|ECONN|Failed/i.test(msg) ? t("menu.err.connect") : errorMessage(err));
    } finally {
      btn?.removeAttribute("aria-busy");
    }
  };

  root.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((b) =>
    b.addEventListener("click", () => void enter(b.dataset.mode as Mode, {}, b)),
  );
  root.querySelector<HTMLFormElement>("[data-friends]")!.addEventListener("submit", (e) => {
    e.preventDefault();
    const code = String(new FormData(e.currentTarget as HTMLFormElement).get("code") ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (code.length < 3) return show(t("menu.err.code"));
    void enter("custom", { code }, (e.currentTarget as HTMLElement).querySelector("button")!);
  });

  // voltar para uma partida em andamento (queda, recarregou a página, servidor reiniciou)
  const offerRejoin = () => {
    const box = root.querySelector<HTMLElement>("[data-rejoin]");
    if (!box || box.innerHTML) return;
    box.innerHTML = `<div class="bw-alert bw-alert--info verify">${t("menu.rejoin")} <button class="bw-btn bw-btn--blue">${t("menu.rejoin.button")}</button></div>`;
    const btn = box.querySelector("button")!;
    btn.addEventListener("click", async () => {
      btn.setAttribute("aria-busy", "true");
      const room = await rejoinMatch(undefined, 30_000);
      if (!room) {
        box.innerHTML = "";
        return show(t("menu.rejoin.expired"));
      }
      setCurrentRoom(room);
      const store = new GameStore(room);
      setGameStore(store);
      navigate("/partida");
    });
  };
  if (savedRoom()) offerRejoin();
  void api.currentMatch().then((r) => r.match && offerRejoin()).catch(() => undefined);
  // saiu do menu: sai da fila
  return () => searching?.abort();
};
